/**
 * ReportDownloadMenu — the Reports cell of a Candidates row: ONE "Reports"
 * button that opens a POPOVER FORM (despite the module's name, kept from when
 * it was a menu), pick a report, then ONE [⬇ Download PDF | ▾] whose ▾ holds
 * the format (Jared, 2026-10-27; `ReportDownloadButton` in `reportPicker.tsx`)
 * (epic `2026-10-06-search-candidates-reports`, sub-task 04; four kinds since
 * epic `2026-10-22-candidate-reports-four-kinds`; radio pickers since its
 * sub-task 03, variant "E" of the preview artifact, after Jared's "it may need
 * to all be radio buttons with a submit. its confusing what youre supposed to
 * click", 2026-10-24).
 *
 * A RADIX `Popover`, NOT A `DropdownMenu`: what opens is a form with a radio
 * group and a submit (the format's small ▾ menu inside it IS a menu: three
 * commands, Word / PDF / Text), and a menu is a list of commands — a screen reader in a
 * menu reads only menu items, and the arrow keys would walk items instead of
 * moving within a radio group.
 *
 * `CandidateReportButtons` is the cell: the button when the ROW'S PROJECTION
 * flags at least one kind (`has_recruiter_assessment`,
 * `has_client_assessment`, `references_count > 0`,
 * `backchannel_references_count > 0`), an em dash when it flags none. The
 * projection says whether Thrive HAS the report; the index read
 * (`useCandidateReports`, asked only for a flagged row) says whether a copy is
 * STORED, which decides each radio:
 *
 *   - `present` (or any status) with stored `formats` → enabled;
 *   - `building` WITH `formats` (a rebuild of a report that was stored: the
 *     API keeps the previous build's files downloadable, #1020 iter 2) →
 *     enabled, with a small "Updating…"; a Refresh never hides a valid report;
 *   - `building` with no `formats` (a first build) → disabled, "Preparing…" —
 *     a spinner while it is polled, the stalled sentence once its episode's
 *     poll budget is spent (nothing is being asked any more, so nothing should
 *     spin — the panel offers the Refresh);
 *   - `none` / `deleted`, or no stored format whatever the status → disabled,
 *     "No stored copy yet": the next dashboard build stores it, nothing a
 *     click here could do (`reportMayServe` decides, as it does for the
 *     default selection);
 *   - not answered yet, or the read failed → enabled, every format offered: a
 *     download of a copy that is not there answers 404 and lands in the
 *     toast, which is a better failure than a button that never wakes up;
 *   - a kind the projection does NOT flag → disabled and lighter (`opacity-40`, as the panel), NO visible
 *     line (owner, 2026-10-26: the grey says it); its reason is the label's
 *     `title` (shown over the label text; the disabled dot itself swallows
 *     the pointer in Chromium) and a
 *     screen-reader-only part of the radio's name.
 *
 * Every other disabled radio says WHY in visible secondary text under its name,
 * INSIDE its label (so it is read once, as part of the radio's name — not
 * again as a description), not only in a `title`: a native disabled radio is
 * skipped by the keyboard and swallows the pointer, so a tooltip would be the
 * one place the reason lives and nobody would reach it. The ONE exception is
 * a kind Thrive does not have (above): there the owner accepted the trade-off
 * (2026-10-26, PR #1094) — the lighter grey is the message, keyboard and touch
 * users see only that, and the sentence stays in the radio's name for screen
 * readers and in the label's tooltip for a mouse over the label text. Do not
 * copy that pattern to a state the user can act on.
 *
 * The REPORT selection persists across opens (the component stays mounted),
 * so the popover focuses the CHECKED radio when it opens, the radiogroup
 * convention. The FORMAT restarts from the reader's remembered one on each
 * open, so a pick made in another row or in the panel reaches this one (kept
 * for the page when storage is blocked).
 * Should the index answer while the form is open and leave nothing to
 * download, the form closes for good (a later poll does not reopen it) and
 * focus goes to the unavailable button that replaces the trigger.
 *
 * When NO flagged kind can serve a file (each a first build, or not stored),
 * the button itself is unavailable and says why — "Preparing…" or its word —
 * rather than opening a form of dead radios. UNAVAILABLE IS `aria-disabled`,
 * NOT `disabled`: a natively disabled button swallows the pointer, so its
 * `title` — the only place the reason is said — never shows in Chromium
 * (/pr-review #1021 iter 1). The click is a no-op.
 *
 * The popover is portalled, and React bubbles a portalled click through the
 * component tree to the row — `CandidatesTable` ignores clicks whose target is
 * outside the row's own DOM for that reason, so picking a radio never also
 * opens the panel.
 *
 * `CandidateReportButtons` is the cell around it. The FORMAT second level,
 * the shared wording and the reasons live in `reportPicker.tsx`, which the
 * candidate panel imports too.
 *
 * Design-system token utilities only; no inline `style`; English copy only.
 */

import type { SearchCandidateRow } from "@nu-assist/contracts";
import { Spinner } from "@nu-assist/ui";
import * as Popover from "@radix-ui/react-popover";
import { ChevronDown } from "lucide-react";
import {
  type FormEvent,
  Fragment,
  type ReactNode,
  type Ref,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

import {
  CANDIDATE_REPORT_KINDS,
  type CandidateReportFormat,
  type CandidateReportKind,
  type CandidateReportState,
  type CandidateReportStatus,
  type CandidateReportsClient,
  REPORT_GROUPS,
  REPORT_KIND_LABEL,
  REPORT_KIND_MISSING_REASON,
  defaultReportKind,
  reportMayServe,
  rowHasReport,
  rowRefereeCount,
} from "../../../clients/candidateReports.client";
import {
  FORMAT_NOT_STORED_REASON,
  NOT_STORED_TITLE,
  REPORT_FORMATS,
  ReportDownloadButton,
  STALLED_TITLE,
  pickerCaptionClass,
  preferredReportFormat,
  readRememberedReportFormat,
  refereesLabel,
  rememberReportFormat,
} from "./reportPicker";
import {
  UPDATING_FROM_THRIVE,
  reportStateFor,
  useCandidateReports,
  useReportDownload,
} from "./useCandidateReports";

const triggerClass =
  "inline-flex items-center gap-1 whitespace-nowrap rounded-sm border border-border bg-surface px-2 py-1 text-caption text-text hover:border-accent hover:text-accent-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent aria-disabled:cursor-not-allowed aria-disabled:opacity-60 aria-disabled:hover:border-border aria-disabled:hover:text-text";

/** `data-status` on a radio and the button: the index status, or ours. */
type KindStatus = CandidateReportStatus | "missing" | "stalled" | "unknown";

/** One report's word on whether, and why not, it can be picked. */
interface KindAvailability {
  kind: CandidateReportKind;
  enabled: boolean;
  status: KindStatus;
  /** The visible line under the name: a reason, or the "Updating…" note. */
  note: { testId: string; text: ReactNode; title?: string } | null;
  /**
   * A kind Thrive does not have: no visible line (owner, 2026-10-26 — the
   * lighter grey says it), the reason only as the label's tooltip and in the
   * radio's accessible name.
   */
  quietReason?: string;
}

function availability(
  kind: CandidateReportKind,
  offered: boolean,
  state: CandidateReportState | undefined,
  stalled: boolean
): KindAvailability {
  const testId = `report-menu-${kind}`;
  if (!offered) {
    return {
      kind,
      enabled: false,
      status: "missing",
      note: null,
      quietReason: REPORT_KIND_MISSING_REASON[kind],
    };
  }
  if (state === undefined) return { kind, enabled: true, status: "unknown", note: null };
  if (state.status === "building" && state.formats.length > 0) {
    return {
      kind,
      enabled: true,
      status: "building",
      note: {
        testId: `${testId}-updating`,
        text: "Updating…",
        title: stalled
          ? "A newer version is still being prepared — open the candidate and try Refresh from Thrive."
          : "A newer version is being prepared; this one stays downloadable.",
      },
    };
  }
  if (state.status === "building") {
    const name = REPORT_KIND_LABEL[kind].toLowerCase();
    return {
      kind,
      enabled: false,
      status: stalled ? "stalled" : "building",
      note: {
        testId: `${testId}-preparing`,
        text: stalled ? (
          STALLED_TITLE
        ) : (
          <>
            {/* Decorative: the line's own text already says it, and a
                labelled `<output>` here would nest a live region in the
                radio's description (PR #1088 iter 2). */}
            <span aria-hidden="true" className="inline-flex">
              <Spinner size="sm" label={`Preparing the ${name}`} />
            </span>
            Preparing…
          </>
        ),
      },
    };
  }
  // `none` / `deleted`, or a status that claims a copy with no stored format:
  // what can be downloaded is `formats`, whatever the status.
  if (!reportMayServe(state)) {
    return {
      kind,
      enabled: false,
      status: state.status,
      note: { testId: `${testId}-not-stored`, text: NOT_STORED_TITLE },
    };
  }
  return { kind, enabled: true, status: state.status, note: null };
}

/**
 * One radio of the Report group: full name, referee count, the visible note.
 * The note is inside the label, so it is part of the radio's accessible name
 * and is NOT also its description (it would be read twice).
 */
function KindOption({
  name,
  item,
  checked,
  onPick,
  refereeCount,
}: {
  name: string;
  item: KindAvailability;
  checked: boolean;
  onPick: () => void;
  refereeCount: number | null;
}) {
  const testId = `report-menu-${item.kind}`;
  const count = refereeCount !== null && refereeCount > 0 ? refereeCount : null;
  return (
    <label
      title={item.quietReason}
      data-testid={item.quietReason ? `${testId}-missing` : undefined}
      className={`flex cursor-pointer items-start gap-2 rounded-sm px-2 py-1.5 text-caption text-text hover:bg-row-hover has-[:checked]:bg-accent-muted has-[:checked]:font-semibold has-[:disabled]:cursor-not-allowed has-[:disabled]:text-text-muted has-[:disabled]:hover:bg-transparent has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent${
        item.quietReason ? " opacity-40" : ""
      }`}
    >
      <input
        type="radio"
        name={name}
        value={item.kind}
        checked={checked}
        disabled={!item.enabled}
        onChange={onPick}
        data-testid={testId}
        data-status={item.status}
        className="m-0 mt-0.5 h-3.5 w-3.5 shrink-0 cursor-pointer accent-accent disabled:cursor-not-allowed focus-visible:outline-none"
      />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="flex items-baseline gap-3">
          <span>{REPORT_KIND_LABEL[item.kind]}</span>
          {item.quietReason ? <span className="sr-only">{` — ${item.quietReason}`}</span> : null}
          {count !== null ? (
            <span className="ml-auto font-normal tabular-nums text-text-secondary">
              <span aria-hidden="true" data-testid={`${testId}-referees`}>
                {count}
              </span>
              <span className="sr-only">{refereesLabel(count)}</span>
            </span>
          ) : null}
        </span>
        {item.note ? (
          <span
            data-testid={item.note.testId}
            title={item.note.title}
            className="inline-flex items-center gap-1.5 font-normal text-text-secondary"
          >
            {item.note.text}
          </span>
        ) : null}
      </span>
    </label>
  );
}

/** A button that says why it does nothing — see the module note. */
function UnavailableButton({
  status,
  title,
  buttonRef,
  children,
}: {
  status: KindStatus;
  title: string;
  buttonRef?: Ref<HTMLButtonElement>;
  children: ReactNode;
}) {
  return (
    <button
      ref={buttonRef}
      type="button"
      aria-disabled="true"
      data-testid="report-menu-button"
      data-status={status}
      title={title}
      onClick={(e) => e.preventDefault()}
      className={triggerClass}
    >
      {children}
    </button>
  );
}

export interface ReportDownloadMenuProps {
  /** The kinds the row's projection flags (at least one). */
  offered: readonly CandidateReportKind[];
  /** The index's word on a kind; undefined while unanswered or failed. */
  stateOf: (kind: CandidateReportKind) => CandidateReportState | undefined;
  /** The kind and format whose signed URL is being asked for, if any;
   *  `waiting` while the click waits for its report's rebuild (#874). */
  busy: { kind: CandidateReportKind; format: CandidateReportFormat; waiting?: boolean } | null;
  onDownload: (kind: CandidateReportKind, format: CandidateReportFormat) => void;
  /** For the accessible name: "Download Ana Álvarez’s reports". */
  candidateName?: string | null;
  /** A kind building past its poll budget: nothing is being asked any more. */
  stalled: (kind: CandidateReportKind) => boolean;
  /** The row's referee count for a references kind; null for an assessment. */
  refereeCount: (kind: CandidateReportKind) => number | null;
  /** The index has answered: the button reports `ready` instead of `unknown`. */
  answered: boolean;
}

export function ReportDownloadMenu({
  offered,
  stateOf,
  busy,
  onDownload,
  candidateName,
  stalled,
  refereeCount,
  answered,
}: ReportDownloadMenuProps) {
  const [open, setOpen] = useState(false);
  const unavailableRef = useRef<HTMLButtonElement | null>(null);
  const kindsRef = useRef<HTMLDivElement | null>(null);
  const [chosenKind, setChosenKind] = useState<CandidateReportKind | null>(null);
  const [chosenFormat, setChosenFormat] = useState<CandidateReportFormat | null>(null);
  // The reader's last pick in this browser is the preferred default, shared
  // with the panel and every other row. Re-read on each open: a pick made in
  // another row or in the panel since this row rendered must reach it
  // (/pr-review #1102 iter 1). It is a default, pinned like any other.
  const [remembered, setRemembered] = useState(readRememberedReportFormat);
  const pickFormat = (next: CandidateReportFormat) => {
    setChosenFormat(next);
    rememberReportFormat(next);
  };
  const kindName = useId();
  const captionId = useId();
  const who = candidateName ? `${candidateName}’s` : "this candidate’s";

  const items = CANDIDATE_REPORT_KINDS.map((kind) =>
    availability(kind, offered.includes(kind), stateOf(kind), stalled(kind))
  );
  const enabledKinds = items.filter((i) => i.enabled).map((i) => i.kind);
  const canServe = enabledKinds.length > 0;

  const kind =
    chosenKind !== null && enabledKinds.includes(chosenKind)
      ? chosenKind
      : defaultReportKind(enabledKinds, stateOf);
  const state = kind ? stateOf(kind) : undefined;
  // An unanswered index offers every format (see the module note).
  const formats: readonly CandidateReportFormat[] = state
    ? state.formats
    : REPORT_FORMATS.map((f) => f.format);
  const [pinnedFormat, setPinnedFormat] = useState<{
    kind: CandidateReportKind;
    format: CandidateReportFormat;
  } | null>(null);
  const format =
    chosenFormat !== null && formats.includes(chosenFormat)
      ? chosenFormat
      : pinnedFormat?.kind === kind && formats.includes(pinnedFormat.format)
        ? pinnedFormat.format
        : preferredReportFormat(formats, remembered);

  // The defaults the open form shows, once the index has answered, are pinned
  // (/pr-review #1092 N1): a poll that makes another kind servable, or adds a
  // format, must not switch the selection under a user who never picked. It
  // moves only when the pinned value stops being offered. While the form is
  // shut the default keeps following the index, so it opens on the freshest.
  useEffect(() => {
    if (!open || !answered) return;
    if (chosenKind === null && kind) setChosenKind(kind);
    if (chosenFormat === null && kind && format && pinnedFormat?.kind !== kind) {
      setPinnedFormat({ kind, format });
    }
  }, [open, answered, chosenKind, kind, chosenFormat, format, pinnedFormat]);

  // The index answered while the form was open and left nothing to download:
  // the form is gone (the button below replaced the trigger). Close it for
  // good, so a later poll that makes a kind servable does not pop it open by
  // itself, and hand focus to the button that says why, not to <body>.
  useEffect(() => {
    if (canServe || !open) return;
    setOpen(false);
    // Focus lands on the unavailable button, not the trigger: the trigger has
    // already unmounted with Popover.Root, so Radix's own restore targets a
    // detached node and this focus wins.
    unavailableRef.current?.focus();
  }, [canServe, open]);

  if (!canServe) {
    const building = offered.filter((kind) => stateOf(kind)?.status === "building");
    if (building.length > 0) {
      const allStalled = building.every((kind) => stalled(kind));
      return (
        <UnavailableButton
          buttonRef={unavailableRef}
          status={allStalled ? "stalled" : "building"}
          title={allStalled ? STALLED_TITLE : "The reports are being stored from Thrive."}
        >
          {allStalled ? null : <Spinner size="sm" label={`Preparing ${who} reports`} />}
          Preparing…
        </UnavailableButton>
      );
    }
    return (
      <UnavailableButton
        buttonRef={unavailableRef}
        status={offered.map((kind) => stateOf(kind)?.status).find(Boolean) ?? "none"}
        title={NOT_STORED_TITLE}
      >
        Reports
      </UnavailableButton>
    );
  }

  const updating = items.some((i) => i.status === "building" && i.enabled);
  const isBusy = busy !== null && offered.includes(busy.kind);
  // The click is waiting for its report's rebuild from Thrive (#874).
  const waiting = isBusy && busy?.waiting === true;
  const canSubmit = kind !== null && format !== null && busy === null;

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit || !kind || !format) return;
    onDownload(kind, format);
    setOpen(false);
  }

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        // A download in flight keeps the form shut, like the button's spinner says.
        if (next && isBusy) return;
        if (next) {
          // Each open starts from the reader's current preference. With
          // storage blocked there is none to read: keep this row's own pick
          // for the rest of the page, as the report choice is kept.
          const preference = readRememberedReportFormat();
          if (preference !== null) {
            setRemembered(preference);
            setChosenFormat(null);
            setPinnedFormat(null);
          }
        }
        setOpen(next);
      }}
    >
      <Popover.Trigger asChild>
        <button
          type="button"
          data-testid="report-menu-button"
          data-status={answered ? "ready" : "unknown"}
          // The accessible name REPLACES the content, so "Updating…" has to be
          // in it too, or a screen reader never hears it.
          aria-label={`Download ${who} reports${
            waiting
              ? ` (${UPDATING_FROM_THRIVE})`
              : updating
                ? " (a newer version is being prepared)"
                : ""
          }`}
          aria-busy={isBusy || undefined}
          aria-disabled={isBusy || undefined}
          className={triggerClass}
        >
          {isBusy ? (
            <Spinner size="sm" label={waiting ? UPDATING_FROM_THRIVE : "Downloading"} />
          ) : null}
          Reports
          {waiting ? (
            <span data-testid="report-menu-waiting" className="text-label text-text-secondary">
              {UPDATING_FROM_THRIVE}
            </span>
          ) : updating ? (
            <span data-testid="report-menu-updating" className="text-label text-text-secondary">
              Updating…
            </span>
          ) : null}
          <ChevronDown className="h-3 w-3 text-text-secondary" aria-hidden="true" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          aria-label={`Download ${who} reports`}
          data-testid="report-menu-content"
          collisionPadding={8}
          // Escape inside the format ▾ menu closes that menu only. Both are
          // dismissable layers listening on the document; once the menu has
          // removed itself the form reads as the top layer and would close
          // on the same key press (/pr-review #1102 iter 1).
          onEscapeKeyDown={(e) => {
            if (e.target instanceof Element && e.target.closest('[role="menu"]'))
              e.preventDefault();
          }}
          // The report selection persists across opens: focus the CHECKED radio,
          // not the first enabled one Radix would pick (/pr-review #1092).
          onOpenAutoFocus={(e) => {
            const checked = kindsRef.current?.querySelector<HTMLInputElement>("input:checked");
            if (!checked) return;
            e.preventDefault();
            checked.focus();
          }}
          className="z-50 w-80 rounded-md border border-border bg-surface p-3 shadow-lg"
        >
          <form onSubmit={submit} className="flex flex-col gap-2.5">
            <div className="flex flex-col gap-1">
              <span id={captionId} className={pickerCaptionClass}>
                Report
              </span>
              <div
                ref={kindsRef}
                role="radiogroup"
                aria-labelledby={captionId}
                data-testid="report-menu-kinds"
                className="flex flex-col rounded-md border border-border p-1"
              >
                {REPORT_GROUPS.map((group, index) => (
                  <Fragment key={group.id}>
                    {index > 0 ? <hr className="mx-0.5 my-1 border-border" /> : null}
                    <span aria-hidden="true" className={`${pickerCaptionClass} px-2 pb-0.5 pt-1`}>
                      {group.label}
                    </span>
                    {/* `items` is in CANDIDATE_REPORT_KINDS order, which the groups
                        flatten to (pinned in candidateReports.client.test.ts). */}
                    {items
                      .filter((i) => group.kinds.includes(i.kind))
                      .map((item) => (
                        <KindOption
                          key={item.kind}
                          name={kindName}
                          item={item}
                          checked={item.kind === kind}
                          onPick={() => setChosenKind(item.kind)}
                          refereeCount={refereeCount(item.kind)}
                        />
                      ))}
                  </Fragment>
                ))}
              </div>
            </div>
            <div className="flex border-t border-border pt-2.5">
              <ReportDownloadButton
                format={format}
                offered={formats}
                reason={() => FORMAT_NOT_STORED_REASON}
                onFormatChange={pickFormat}
                canDownload={canSubmit}
                reportName={kind ? REPORT_KIND_LABEL[kind] : "report"}
                testId="report-menu-download"
                formatTestIdPrefix="report-menu-format"
              />
            </div>
          </form>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export interface CandidateReportButtonsProps {
  row: SearchCandidateRow;
  client: CandidateReportsClient;
  searchId: string;
}

/**
 * The Reports cell of one row — a single Reports button opening the popover
 * form above (the name is from when the cell held one button per kind); an
 * em dash when the projection flags nothing.
 */
export function CandidateReportButtons({ row, client, searchId }: CandidateReportButtonsProps) {
  const candidacyId = row.candidacy_id == null ? "" : String(row.candidacy_id);
  const kinds = CANDIDATE_REPORT_KINDS.filter((kind) => rowHasReport(row, kind));
  const { query, stalled } = useCandidateReports({
    client,
    searchId,
    candidacyId,
    enabled: kinds.length > 0,
  });
  const { busy, download } = useReportDownload(client, searchId, candidacyId);

  if (kinds.length === 0 || !candidacyId) {
    return <span className="text-text-muted">—</span>;
  }
  return (
    <ReportDownloadMenu
      offered={kinds}
      stateOf={(kind) => reportStateFor(query.data, kind)}
      busy={busy}
      onDownload={(kind, format) => void download(kind, format)}
      candidateName={row.name}
      stalled={stalled}
      refereeCount={(kind) => rowRefereeCount(row, kind)}
      answered={query.data !== undefined}
    />
  );
}
